import { SculptureEdition } from "@/lib/types";
import {
  AdvancedSearchConfig,
  AdvancedSearchFieldConfig,
  attachmentPhotoAdvancedSearchGroup,
} from "../forms/advanced-search/advanced-search-fields-def";
import {
  artistLastNameSortGroup,
  masterArtworkRelatedAuthorLastNameSortField,
} from "../forms/advanced-search/artist-sort-fields";
import { ColumnFormatted } from "./general";

/** Collection actuelle + historique des exemplaires (onglet Historique du formulaire exemplaire). */
export const sculptureEditionProvenanceAdvancedSearchFields: AdvancedSearchFieldConfig[] =
  [
    {
      type: "enum",
      label: "collector",
      key: "collector.collectionneursDescriptionNom",
      optionsFrom: "collectors",
    },
    {
      type: "boolean",
      key: "nonLocalise",
      label: "historique.sculpture.collection.nonLocalise",
    },
    {
      type: "string",
      key: "history.some.description",
      label: "sculpture.history.description",
    },
    {
      type: "string",
      key: "history.some.villePays",
      label: "sculpture.history.villePays",
    },
    {
      type: "string",
      key: "history.some.dates",
      label: "sculpture.history.dates",
    },
    {
      type: "boolean",
      key: "history.some.collectionParticuliere",
      label: "sculpture.history.collectionParticuliere",
    },
  ];

export const withMasterArtworkSculptureEditionSearchKeys = (
  fields: AdvancedSearchFieldConfig[],
): AdvancedSearchFieldConfig[] =>
  fields.map((field) => ({
    ...field,
    key: `masterForSculptureEditions.some.${field.key}`,
  }));

export const sculptureEditionsColumns: ColumnFormatted<SculptureEdition>[] = [
  {
    type: "string",
    key: "sculptureEditionNumeroEnregistrement",
    visible: true,
  },
  { type: "image", key: "vignette", visible: true },
  { type: "string", key: "sculptureEditionNumeroInventaire", visible: true },
  { type: "string", key: "sculptureEditionAutreNumero", visible: false },
  { type: "string", key: "sculptureEditionAncienNumero", visible: false },
  {
    type: "status",
    key: "sculptureEditionPublicationStatus",
    visible: true,
  },
  { type: "string", key: "sculptureEditionStatus", visible: false },
  { type: "string", key: "sculptureEditionTypeEdition", visible: false },
  { type: "string", key: "sculptureEditionDescriptionSocle", visible: false },
  { type: "string", key: "sculptureEditionDateRealisation", visible: false },
  { type: "string", key: "sculptureEditionFondeurFabricant", visible: false },
  { type: "string", key: "sculptureEditionNumeroFonte", visible: false },
  { type: "multilingual", key: "sculptureEditionTechnique", visible: false },
  { type: "multilingual", key: "sculptureEditionMateriau", visible: false },
  { type: "multilingual", key: "sculptureEditionSupport", visible: false },
  { type: "multilingual", key: "sculptureEditionNumerotation", visible: false },
  { type: "multilingual", key: "sculptureEditionSignature", visible: false },
  {
    type: "multilingual",
    key: "sculptureEditionMarqueTampons",
    visible: false,
  },
  {
    type: "boolean",
    key: "sculptureEditionMarqueTamponsPublished",
    visible: false,
  },
  {
    type: "string",
    key: "sculptureEditionEstimationDescription",
    visible: false,
  },
  {
    type: "currency",
    key: "sculptureEditionEstimationMontant",
    visible: false,
  },
  {
    type: "string",
    key: "sculptureEditionAssuranceDescription",
    visible: false,
  },
  { type: "currency", key: "sculptureEditionAssuranceMontant", visible: false },
  { type: "string", key: "sculptureEditionVenteDescription", visible: false },
  { type: "currency", key: "sculptureEditionVenteMontant", visible: false },
  {
    type: "string",
    key: "sculptureEditionAcquisitionDescription",
    visible: false,
  },
  {
    type: "currency",
    key: "sculptureEditionAcquisitionMontant",
    visible: false,
  },
  { type: "richtext", key: "sculptureEditionNotes", visible: false },
  {
    type: "multilingual",
    key: "sculptureEditionRemarquesPubliquesMultilingual",
    visible: false,
  },
  {
    type: "boolean",
    key: "sculptureEditionRemarquesPubliquesPublished",
    visible: false,
  },
  { type: "richtext", key: "sculptureEditionNotesTechniques", visible: false },
  { type: "string", key: "sculptureEditionLocation1", visible: false },
  { type: "string", key: "sculptureEditionLocation2", visible: false },
  { type: "string", key: "sculptureEditionLocation3", visible: false },
  // Dimensions
  { type: "dimensions", key: "sculptureEditionDimensions", visible: false },
  { type: "string", key: "sculptureEditionPoids", visible: false },
  {
    type: "dimensions",
    key: "sculptureEditionDimensionsSocle",
    visible: false,
  },
  { type: "string", key: "sculptureEditionPoidsSocle", visible: false },
  {
    type: "dimensions",
    key: "sculptureEditionDimensionsBrutes",
    visible: false,
  },
  { type: "string", key: "sculptureEditionPoidsBrutes", visible: false },
  {
    type: "dimensions",
    key: "sculptureEditionDimensionsAvecSocle",
    visible: false,
  },
  {
    type: "string",
    key: "sculptureEditionPoidsSoclePiedouche",
    visible: false,
  },
  {
    type: "string",
    key: "sculptureEditionDimensionsPersonnalisees",
    visible: false,
  },
  { type: "string", key: "sculptureEditionPoidsLibre", visible: false },
  { type: "richtext", key: "sculptureEditionNotesPoids", visible: false },
  {
    type: "computed",
    key: "createdAt",
    visible: true,
    formattedData: (data: SculptureEdition) => {
      if (!data.createdAt) return "";
      return new Date(data.createdAt).toLocaleDateString(undefined, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },
  },
  {
    type: "computed",
    key: "updatedAt",
    visible: false,
    formattedData: (data: SculptureEdition) => {
      if (!data.updatedAt) return "";
      return new Date(data.updatedAt).toLocaleDateString(undefined, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },
  },
  { type: "string", key: "createdById", visible: false },
  { type: "string", key: "updatedById", visible: false },
];

export const sculptureEditionsColumnsVisibility =
  sculptureEditionsColumns.reduce(
    (acc, column) => ({
      ...acc,
      [column.key]: column.visible || false,
    }),
    {},
  );

export const sculptureEditionsAdvancedSearchConfig: AdvancedSearchConfig = [
  {
    type: "group",
    label: "sculptureEditions",
    fields: [
      { type: "string", key: "sculptureEditionNumeroEnregistrement" },
      { type: "string", key: "sculptureEditionNumeroInventaire" },
      { type: "string", key: "sculptureEditionAutreNumero" },
      { type: "string", key: "sculptureEditionAncienNumero" },
      { type: "string", key: "sculptureEditionPublicationStatus" },
      { type: "string", key: "sculptureEditionStatus" },
      { type: "string", key: "sculptureEditionTypeEdition" },
      { type: "string", key: "sculptureEditionDescriptionSocle" },
      { type: "string", key: "sculptureEditionDateRealisation" },
      { type: "string", key: "sculptureEditionFondeurFabricant" },
      { type: "string", key: "sculptureEditionNumeroFonte" },
      { type: "multilingual", key: "sculptureEditionTechnique" },
      { type: "multilingual", key: "sculptureEditionMateriau" },
      { type: "multilingual", key: "sculptureEditionSupport" },
      { type: "multilingual", key: "sculptureEditionNumerotation" },
      { type: "multilingual", key: "sculptureEditionSignature" },
      { type: "multilingual", key: "sculptureEditionMarqueTampons" },
      { type: "boolean", key: "sculptureEditionMarqueTamponsPublished" },
      { type: "string", key: "sculptureEditionEstimationDescription" },
      { type: "currency", key: "sculptureEditionEstimationMontant" },
      { type: "string", key: "sculptureEditionAssuranceDescription" },
      { type: "currency", key: "sculptureEditionAssuranceMontant" },
      { type: "string", key: "sculptureEditionVenteDescription" },
      { type: "currency", key: "sculptureEditionVenteMontant" },
      { type: "string", key: "sculptureEditionAcquisitionDescription" },
      { type: "currency", key: "sculptureEditionAcquisitionMontant" },
      { type: "string", key: "sculptureEditionNotes" },
      {
        type: "multilingual",
        key: "sculptureEditionRemarquesPubliquesMultilingual",
      },
      { type: "boolean", key: "sculptureEditionRemarquesPubliquesPublished" },
      { type: "string", key: "sculptureEditionNotesTechniques" },
      { type: "string", key: "sculptureEditionLocation1" },
      { type: "string", key: "sculptureEditionLocation2" },
      { type: "string", key: "sculptureEditionLocation3" },
      // Dimensions
      { type: "string", key: "sculptureEditionPoids" },
      { type: "string", key: "sculptureEditionPoidsSocle" },
      { type: "string", key: "sculptureEditionPoidsBrutes" },
      { type: "string", key: "sculptureEditionPoidsSoclePiedouche" },
      { type: "string", key: "sculptureEditionDimensionsPersonnalisees" },
      { type: "string", key: "sculptureEditionPoidsLibre" },
      { type: "string", key: "sculptureEditionNotesPoids" },
      ...sculptureEditionProvenanceAdvancedSearchFields,
    ],
  },
  artistLastNameSortGroup(masterArtworkRelatedAuthorLastNameSortField),
  attachmentPhotoAdvancedSearchGroup,
];
